// src/pages/auth/LoginPage.js
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export function LoginPage() {
  const { signInWithGoogle, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Already logged in
  if (isAuthenticated) {
    navigate(from, { replace: true });
    return null;
  }

  async function handleGoogleSignIn() {
    setLoading(true);
    setError(null);

    const result = await signInWithGoogle();

    if (result.success) {
      navigate(from, { replace: true });
    } else if (result.requiresMfa) {
      navigate('/mfa-setup');
    } else {
      setError(result.error || 'Sign in failed. Make sure you are using your work account.');
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoArea}>
          <span style={styles.logoMark}>⬡</span>
          <h1 style={styles.appName}>ROOTS</h1>
          <p style={styles.tagline}>Compliance Platform</p>
        </div>

        {/* Sign in */}
        <div style={styles.signInArea}>
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            style={{ ...styles.googleBtn, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? (
              <span>Signing in...</span>
            ) : (
              <>
                <GoogleIcon />
                <span>Sign in with Google Workspace</span>
              </>
            )}
          </button>

          {error && <div style={styles.error}>{error}</div>}

          <p style={styles.hint}>
            Use your organization Google account to sign in.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
      <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
      <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
      <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
    </svg>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8f7f4',
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '48px 40px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
    border: '1px solid #e8e4de',
  },
  logoArea: {
    textAlign: 'center',
    marginBottom: 40,
  },
  logoMark: {
    fontSize: 40,
    color: '#7eb86a',
    display: 'block',
    marginBottom: 8,
  },
  appName: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1a1f16',
    margin: 0,
    letterSpacing: '-0.03em',
  },
  tagline: {
    fontSize: 13,
    color: '#8a9485',
    margin: '4px 0 0',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  signInArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '12px 20px',
    background: '#1a1f16',
    color: '#f0ede8',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.15s',
  },
  error: {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
    border: '1px solid #fecaca',
  },
  hint: {
    textAlign: 'center',
    fontSize: 12,
    color: '#aaa69e',
    margin: 0,
  },
};
